import React, { useMemo } from "react";

import { Controller, useFormContext, useWatch } from "react-hook-form";

import { aiNodeModelsToOptions } from "src/aiNode/editorComponents/aiNodeModelsToOptions";
import { AiNodeQuota, AiNodeV2Form, BasicModelCard } from "src/aiNode/types";
import { useAgentModels } from "src/api/agents/management";
import { Card } from "src/base-components/Card";
import { FormItem } from "src/base-components/FormItem";
import { AgentModelInfo, ModelProvider } from "src/clients/agent-management";
import {
  filterModels,
  ModelFilterConfig,
  ModelOption,
  ModelPicker,
  ModelQuota,
} from "src/genai/ModelSelector";
import {
  hostingForModel,
  lifecycleFor,
  regionFromRouting,
  successorIdFor,
} from "src/genai/ModelSelector/facets";
import { withByokPricing } from "src/genai/ModelSelector/modelOptions";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

// Providers for which a workspace can hold a BYOK credential (see the AI
// credentials settings). Bedrock is intentionally absent: it's Taktile's shared
// hosting, not a customer BYOK credential type, so agents `/models` never
// returns a Bedrock row with a credential_id.
const AI_NODE_BYOK_PROVIDERS: ReadonlySet<ModelProvider> =
  new Set<ModelProvider>([
    ModelProvider.OPENAI,
    ModelProvider.GOOGLE_VERTEX,
    ModelProvider.ANTHROPIC,
  ]);

const BYOK_MARKER = ":byok:";

const MODELS_BEHIND_FEATURE_FLAGS: Record<string, FEATURE_FLAGS> = {};

const computeModelVisibility = (
  models: BasicModelCard[],
): Record<string, boolean> => {
  const values: Record<string, boolean> = {};

  // Per-model feature flags
  Object.entries(MODELS_BEHIND_FEATURE_FLAGS).map(([modelId, flag]) => {
    values[modelId] = isFeatureFlagEnabled(flag);
  });

  // WIP models share one flag
  const showWip = isFeatureFlagEnabled(
    FEATURE_FLAGS.aiNodeWorkInProgressModels,
  );
  models
    .filter((m) => m.status === "wip")
    .map((m) => {
      values[m.model_id] = showWip;
    });

  return values;
};

/**
 * Build the compound model option ID used to highlight the selected model.
 * BYOK entries use "model_id:byok:credential_id" so the dropdown can render
 * multiple variants of the same `model_id` (Taktile-hosted + one per
 * credential) as distinct entries. Taktile defaults use plain `model_id`.
 */
const buildSelectedOptionId = (
  modelId: string,
  credentialId: string | null | undefined,
): string => {
  if (credentialId) {
    return `${modelId}${BYOK_MARKER}${credentialId}`;
  }
  return modelId;
};

/**
 * Parse a compound model option ID back into model and credential_id.
 * "gpt-5:byok:uuid" → { modelId: "gpt-5", credentialId: "uuid" }
 * "gpt-5" → { modelId: "gpt-5", credentialId: null }
 */
const parseSelectedOptionId = (
  optionId: string,
): { modelId: string; credentialId: string | null } => {
  const idx = optionId.indexOf(BYOK_MARKER);
  if (idx !== -1) {
    return {
      modelId: optionId.slice(0, idx),
      credentialId: optionId.slice(idx + BYOK_MARKER.length),
    };
  }
  return { modelId: optionId, credentialId: null };
};

type ModelSelectorProps = {
  models: BasicModelCard[];
  quotas: AiNodeQuota;
  immutable: boolean;
};

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  models,
  quotas,
  immutable,
}) => {
  const { setValue, control } = useFormContext<AiNodeV2Form>();
  // Subscribe to credential_id changes so the component re-renders when
  // switching between shared-key and BYOK variants of the same model.
  // getValues() would only read once and miss updates when the model_id stays
  // the same but the credential flips.
  const selectedModelId = useWatch({ control, name: "model.model_id" });
  const selectedCredentialId = useWatch({
    control,
    name: "model.credential_id",
  });

  // Compute model visibility (feature flags + WIP)
  // Not memoized because isFeatureFlagEnabled reads from URL params which can change mid-session
  const modelVisibility = computeModelVisibility(models);

  // BYOK feature gate. When off, no agent-management round-trip happens; the
  // dropdown renders the Taktile-hosted list only — the historical behavior.
  const isByokEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.byokLlmCredentials);

  // BYOK entries source: the agents module's `/models` endpoint already
  // returns Taktile + BYOK rows with `credential_id`/`credential_name`
  // attached. We reuse that here (filtered to BYOK + AI-node-supported
  // providers) rather than introducing a new endpoint. On workspaces without
  // the agents module deployed, the query silently fails and BYOK options
  // simply don't appear — matching the product rule that BYOK in AI nodes
  // requires the agents module.
  const {
    data: agentModels,
    isSuccess: byokModelsLoaded,
    isLoading: byokModelsLoading,
  } = useAgentModels({
    enabled: isByokEnabled,
  });

  const byokModelOptions: ModelOption[] = useMemo(() => {
    // Gate on `isByokEnabled`, not just `agentModels`: React Query keeps the
    // cached `/models` result around even while the query is disabled, so a
    // workspace that visited the agents area earlier in the session would
    // otherwise leak BYOK entries into the dropdown after the flag is off.
    if (!isByokEnabled || !agentModels) return [];
    return agentModels
      .filter(
        (m) => !!m.credential_id && AI_NODE_BYOK_PROVIDERS.has(m.provider),
      )
      .map(convertAgentBYOKModelToModelOption);
  }, [isByokEnabled, agentModels]);

  // We can only resolve a BYOK selection to a real dropdown option when BYOK is
  // enabled AND the agents `/models` query actually succeeded. If the flag is
  // off, or the query is loading/errored (e.g. agents module unavailable),
  // there's no BYOK option to match against.
  const canResolveByok = isByokEnabled && byokModelsLoaded;

  // While the agents `/models` query is still in flight, a persisted BYOK
  // selection can't be matched to an option yet — its compound id only exists
  // once the query resolves and `byokModelOptions` is populated. Flag this so
  // the picker renders a neutral skeleton for the sub-second load window after
  // a reload, instead of flashing a misleading red "Unavailable" pill. Scoped
  // to `isLoading` (not `!isSuccess`) so a genuinely errored query still falls
  // through to the normal unavailable/deleted handling.
  const isResolvingByokSelection =
    isByokEnabled && byokModelsLoading && !!selectedCredentialId;

  // Selected id can be a Taktile id (plain) or a BYOK compound id. We only use
  // the compound id when BYOK is resolvable — otherwise we render the base
  // model id. The node may still carry a `credential_id` (it's preserved in the
  // form and sent to the backend), but surfacing a compound id we can't match
  // would make the picker render a misleading red "Unavailable" pill on a
  // perfectly valid credential.
  const selectedOptionId = canResolveByok
    ? buildSelectedOptionId(selectedModelId, selectedCredentialId)
    : selectedModelId;

  // Build filter config for the generic filtering utility.
  // `filterModels` uses `selectedModelId` only for its "always keep the
  // selected option visible" escape hatch, and that check runs against the
  // Taktile options (plain ids). Pass the plain `selectedModelId` here, NOT the
  // BYOK compound id: a compound id never matches a Taktile option, which would
  // let the hosted variant of the currently-selected model get filtered out by
  // WIP/feature-flag visibility and block switching back to it. The selected
  // BYOK entry itself is always shown — `byokModelOptions` is appended
  // unconditionally below, never filtered.
  const filterConfig: ModelFilterConfig = useMemo(
    () => ({
      quotas: convertAiNodeQuotasToModelQuotas(quotas),
      selectedModelId,
      modelVisibility,
      quotaDisabledReason:
        "This feature requires the AI Workbench package. Please contact your Solution Engineer to obtain access to this model.",
    }),
    [quotas, selectedModelId, modelVisibility],
  );

  // Adapt the cards to the rich model view-model and apply quota/visibility
  // filtering, then append the BYOK entries. BYOK entries bypass quota
  // filtering — they run on the customer's own provider account and aren't
  // bound by Taktile-side quotas. `withByokPricing` backfills each BYOK entry's
  // cost from its shared sibling (a no-op for the Taktile-hosted entries) so
  // the Compare modal still shows a price.
  const modelOptions: ModelOption[] = useMemo(() => {
    const taktileOptions = filterModels(
      aiNodeModelsToOptions(models, quotas),
      filterConfig,
    ).filter(
      // Anthropic is BYOK-only for AI nodes (mirrors the agents version config,
      // which drops Anthropic entries without a credential). The Taktile-hosted
      // list still surfaces shared Anthropic rows (region "Global", no
      // credential); drop them here. BYOK Anthropic keeps its own entry via
      // `byokModelOptions`, which is appended below and already requires a
      // credential_id.
      (option) => option.provider !== ModelProvider.ANTHROPIC,
    );
    return withByokPricing([...taktileOptions, ...byokModelOptions]);
  }, [models, quotas, filterConfig, byokModelOptions]);

  // If a credential was deleted upstream, the persisted compound id no longer
  // matches any option. Show a friendly fallback label naming the underlying
  // model so the user understands why the field shows "Unavailable".
  // Only treat a credential as deleted once we can actually prove it — i.e. the
  // models query succeeded (`canResolveByok`) and the credential is genuinely
  // absent. A flag-off / loading / errored query is not a deletion: in those
  // cases `selectedOptionId` is the plain model id, which matches its Taktile
  // option, so this is false anyway.
  const isDeletedBYOKCredential =
    canResolveByok &&
    !!selectedCredentialId &&
    !modelOptions.some((m) => m.id === selectedOptionId);
  const fallbackDisplayName = useMemo(() => {
    if (!isDeletedBYOKCredential) return undefined;
    const sibling = modelOptions.find(
      (m) =>
        m.id === selectedModelId ||
        m.id.startsWith(`${selectedModelId}${BYOK_MARKER}`),
    );
    return `${sibling?.displayName ?? selectedModelId} using a deleted custom AI credential`;
  }, [isDeletedBYOKCredential, modelOptions, selectedModelId]);

  const handleSelect = (optionId: string) => {
    const modelOption = modelOptions.find((m) => m.id === optionId);
    if (!modelOption || modelOption.disabled) return;

    const { modelId, credentialId } = parseSelectedOptionId(optionId);
    setValue("model", {
      model_id: modelId,
      provider: modelOption.provider,
      credential_id: credentialId,
    });
    // Global endpoint routing only applies to OpenAI. Reset a stale `true`
    // when switching to a non-OpenAI model, so we don't persist/send an
    // enabled global endpoint for providers that ignore it.
    if (modelOption.provider !== "openai") {
      setValue("inference.use_global_endpoint", false);
    }
  };

  return (
    <Card className="w-full pt-4 pb-4">
      <FormItem>
        <FormItem.Label
          className="mb-2"
          description="Pick an AI model for your task. Each model has different capabilities and performance characteristics."
          isRequired
        >
          Select AI model
        </FormItem.Label>
        <Controller
          control={control}
          name="model.model_id"
          render={() => (
            <div className="w-full">
              <ModelPicker
                dataLoc="ai-node-model-selector"
                isReadOnly={immutable}
                isResolvingSelection={isResolvingByokSelection}
                missingModelDisplayName={fallbackDisplayName}
                models={modelOptions}
                selectedModelId={selectedOptionId}
                onSelect={handleSelect}
              />
            </div>
          )}
        />
      </FormItem>
    </Card>
  );
};

/**
 * Adapt a BYOK row from the agents `/models` endpoint into the picker's
 * {@link ModelOption} view-model. Mirrors the agents version-config converter:
 * the compound id keeps the Taktile-hosted and BYOK variants distinct, and the
 * credential name surfaces as the deployment label (tooltip on the model name).
 */
const convertAgentBYOKModelToModelOption = (
  model: AgentModelInfo,
): ModelOption => ({
  id: buildSelectedOptionId(model.model_id, model.credential_id),
  displayName: model.display_name,
  provider: model.provider,
  lifecycle: lifecycleFor(model),
  hosting: hostingForModel(model),
  region: regionFromRouting(model.routing_target_region),
  byok: Boolean(model.credential_id),
  deploymentLabel: model.credential_id
    ? (model.credential_name ?? undefined)
    : undefined,
  successorId: successorIdFor(model),
  intelligence: model.intelligence,
  speed: model.speed,
  inputCostPer1MTokens: model.input_cost_per_1m_tokens ?? null,
  outputCostPer1MTokens: model.output_cost_per_1m_tokens ?? null,
  contextWindowTokens: model.context_window_size,
  releaseDate: model.release_date,
  // BYOK routes through the customer's own provider, so no Taktile TPM quota.
  tokensPerMinuteQuota: null,
});

const convertAiNodeQuotasToModelQuotas = (
  aiNodeQuotas: AiNodeQuota,
): Record<string, ModelQuota> => {
  const result: Record<string, ModelQuota> = {};

  if (aiNodeQuotas.usage) {
    Object.entries(aiNodeQuotas.usage).map(([modelId, usage]) => {
      result[modelId] = {
        tokenQuota: usage.token_quota,
        tokensUsed: usage.tokens_used ?? undefined,
      };
    });
  }

  return result;
};
